import React, { useEffect, useRef, useState } from 'react';
import { Platform, Pressable, Text, View, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../../../styles/theme';
import { ENTRY_META, EntryIcon } from '../entryMeta';
import { MEALS, rowOf } from '../constants';
import { entryTitle, entrySub, type MealEntry } from '../../../../redux/mealEntry';
import { makeDragStart } from './dragSource';

interface Props {
  sk: number;
  entry: MealEntry;
  resolveTitle: (recipeId: string) => string | undefined;
  active?: boolean;
  onPress: () => void;
}

const isWeb = Platform.OS === 'web';

export function EntryCard({ sk, entry, resolveTitle, active, onPress }: Props) {
  const { t } = useTranslation();
  const m = ENTRY_META[entry.type];
  const skip = entry.type === 'skip';
  const cardRef = useRef<any>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Make the card a drag source on web, mirroring SlotCard. Entry cards (incl.
  // "note libre") are moved between slots the same way recipe cards are — the
  // dragstart payload is the slot key, which SlotGrid's drop zones read back.
  useEffect(() => {
    if (!isWeb) return;
    const el = cardRef.current as HTMLElement | null;
    if (!el) return;

    const handleDragStart = makeDragStart(sk, setIsDragging);
    const handleDragEnd = () => setIsDragging(false);

    el.setAttribute('draggable', 'true');
    el.addEventListener('dragstart', handleDragStart);
    el.addEventListener('dragend', handleDragEnd);

    return () => {
      el.removeAttribute('draggable');
      el.removeEventListener('dragstart', handleDragStart);
      el.removeEventListener('dragend', handleDragEnd);
    };
  }, [sk]);

  return (
    <Pressable
      ref={cardRef}
      onPress={onPress}
      style={[styles.card, skip && styles.cardSkip, active && styles.cardActive, isDragging && styles.cardDragging]}
    >
      <View style={[styles.iconBox, { backgroundColor: m.tint }, skip && styles.iconBoxSkip]}>
        <EntryIcon type={entry.type} size={20} />
      </View>
      {/* A simple meal is a meal: its kicker names the meal, like a recipe
          card's (« SOUPER »), and the type moves to the line underneath. */}
      <Text style={[styles.kicker, { color: m.color }]}>
        {t(entry.type === 'note' ? MEALS[rowOf(sk)].labelKey : m.labelKey).toUpperCase()}
      </Text>
      <Text style={[styles.title, skip && styles.titleSkip]} numberOfLines={2}>
        {entryTitle(entry, resolveTitle, t)}
      </Text>
      <Text style={styles.sub} numberOfLines={1}>{entrySub(entry, t)}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    flex: 1, minHeight: 88, padding: 13, gap: 8, borderRadius: 16,
    backgroundColor: Paprika.surfaceLowest,
    // tonal stacking, warm ambient shadow — no solid border
    shadowColor: 'rgba(82, 68, 60, 0.08)', shadowOpacity: 1, shadowRadius: 8, shadowOffset: { width: 0, height: 2 },
  },
  cardSkip: {
    backgroundColor: 'transparent', borderWidth: 1.5, borderStyle: 'dashed',
    borderColor: Paprika.border, opacity: 0.82, shadowOpacity: 0,
  },
  cardActive: { borderWidth: 1.5, borderColor: Paprika.primary },
  cardDragging: { opacity: 0.4 } as any,
  iconBox: { width: 40, height: 40, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  iconBoxSkip: { borderWidth: 1.5, borderColor: Paprika.border },
  kicker: { fontFamily: 'Inter-SemiBold', fontSize: 9.5, letterSpacing: 0.8 },
  title: { fontFamily: 'Inter-Medium', fontSize: 14, lineHeight: 17, color: Paprika.textPrimary },
  titleSkip: { color: Paprika.textMuted },
  sub: { marginTop: 'auto', fontFamily: 'Inter-Regular', fontSize: 11.5, color: Paprika.textMuted },
});
